import { Injectable, inject } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { environment } from '../../environments/environment';

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
  }
}

/**
 * List of gtag event names used in this service (for GA4 Reports > Engagement > Events).
 *
 * General:
 *   - page_view                  (auto on route change)
 *   - NHA_login              (sign-in)
 *   - NHA_registration       (register button / flow)
 *   - NHA_contact_form       (contact form submit)
 *
 * NHA_dataset group (event_group = NHA_dataset):
 *   - NHA_dataset_click      (click to open dataset)
 *   - NHA_request_access    (request access submit)
 *   - NHA_dataset_download  (download: main or Get Data CSV)
 *   - NHA_dataset_get_data   (Get Data button)
 *   - NHA_dataset_view_file (View File button)
 *   - NHA_dataset_data_descriptors
 *   - NHA_dataset_sample_data
 *   - NHA_dataset_view_code
 *
 * Catalogue:
 *   - NHA_model_click       (click to open model)
 *   - NHA_challenge_click  (click to open challenge)
 *   - NHA_discussion_create (create new discussion post)
 *
 * Consumer:
 *   - NHA_my_project_view  (My Projects page view)
 *   - NHA_my_project_notebook_start
 *   - NHA_my_project_notebook_stop
 *   - NHA_my_project_notebook_create
 *   - NHA_my_project_notebook_delete
 *   - NHA_my_project_request_credits
 *
 * Profile:
 *   - NHA_profile_change_password
 *   - NHA_profile_get_credentials
 *   - NHA_profile_delete_account
 */
const MAHAAGX_GTAG_EVENTS = {
  PAGE_VIEW: 'page_view',
  LOGIN: 'NHA_login',
  REGISTRATION: 'NHA_registration',
  CONTACT_FORM: 'NHA_contact_form',
  DATASET_CLICK: 'NHA_dataset_click',
  REQUEST_ACCESS: 'NHA_request_access',
  DATASET_DOWNLOAD: 'NHA_dataset_download',
  DATASET_GET_DATA: 'NHA_dataset_get_data',
  DATASET_VIEW_FILE: 'NHA_dataset_view_file',
  DATASET_DATA_DESCRIPTORS: 'NHA_dataset_data_descriptors',
  DATASET_SAMPLE_DATA: 'NHA_dataset_sample_data',
  DATASET_VIEW_CODE: 'NHA_dataset_view_code',
  MODEL_CLICK: 'NHA_model_click',
  CHALLENGE_CLICK: 'NHA_challenge_click',
  DISCUSSION_CREATE: 'NHA_discussion_create',
  MY_PROJECT_VIEW: 'NHA_my_project_view',
  MY_PROJECT_NOTEBOOK_START: 'NHA_my_project_notebook_start',
  MY_PROJECT_NOTEBOOK_STOP: 'NHA_my_project_notebook_stop',
  MY_PROJECT_NOTEBOOK_CREATE: 'NHA_my_project_notebook_create',
  MY_PROJECT_NOTEBOOK_DELETE: 'NHA_my_project_notebook_delete',
  MY_PROJECT_REQUEST_CREDITS: 'NHA_my_project_request_credits',
  PROFILE_CHANGE_PASSWORD: 'NHA_profile_change_password',
  PROFILE_GET_CREDENTIALS: 'NHA_profile_get_credentials',
  PROFILE_DELETE_ACCOUNT: 'NHA_profile_delete_account',
} as const;

/** Event group value for all dataset-related events. Use in GA4 as a custom dimension to filter/group (e.g. event_group = NHA_dataset). */
const MAHAAGX_DATASET_EVENT_GROUP = 'NHA_dataset';

/**
 * Sends page views to Google Analytics on each route change (SPA).
 * The GA4 script in index.html handles the initial load; this service
 * ensures every Angular navigation is recorded as a page_view.
 */
@Injectable({
  providedIn: 'root',
})
export class GoogleAnalyticsService {
  private router = inject(Router);

  /**
   * Subscribe to router navigation and send page_view to GA when measurement ID is set.
   */
  initialize(): void {
    const measurementId = environment.googleAnalyticsId;
    if (!measurementId || typeof window.gtag !== 'function') {
      return;
    }

    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        const pagePath = event.urlAfterRedirects || event.url;
        window.gtag!('event', MAHAAGX_GTAG_EVENTS.PAGE_VIEW, {
          page_path: pagePath,
          page_title: document.title,
        });
      });
  }

  /**
   * Send a sign-in event to GA4 (event name: NHA_login).
   * In GA4: use Reports > Engagement > Events, or create an Exploration
   * with Date + Event count for "NHA_login" to get "Sign In / day".
   */
  trackSignIn(method = 'Keycloak'): void {
    if (typeof window.gtag !== 'function') {
      return;
    }
    const measurementId = environment.googleAnalyticsId;
    const params: { method: string; send_to?: string } = { method };
    if (measurementId) {
      params.send_to = measurementId;
    }
    window.gtag('event', MAHAAGX_GTAG_EVENTS.LOGIN, params);
  }

  /**
   * Send a registration event to GA4 (event name: NHA_registration).
   * Call this when a user completes registration (e.g. after Keycloak sign-up).
   * In GA4: use Reports > Engagement > Events, or create an Exploration
   * with Date + Event count for "NHA_registration" to get "Registrations / day".
   */
  trackRegistration(method = 'Keycloak'): void {
    if (typeof window.gtag !== 'function') {
      return;
    }
    const measurementId = environment.googleAnalyticsId;
    const params: { method: string; send_to?: string } = { method };
    if (measurementId) {
      params.send_to = measurementId;
    }
    window.gtag('event', MAHAAGX_GTAG_EVENTS.REGISTRATION, params);
  }

  /**
   * Send a contact form submission event to GA4 (event name: NHA_contact_form).
   * In GA4: use Reports > Engagement > Events, or create an Exploration
   * with Date + Event count for "NHA_contact_form" to get "Contact form submissions / day".
   */
  trackContactFormSubmit(): void {
    if (typeof window.gtag !== 'function') {
      return;
    }
    const measurementId = environment.googleAnalyticsId;
    const params: Record<string, string> = {};
    if (measurementId) {
      params['send_to'] = measurementId;
    }
    window.gtag('event', MAHAAGX_GTAG_EVENTS.CONTACT_FORM, params);
  }

  /**
   * Send a dataset click event to GA4 (event name: NHA_dataset_click).
   * Part of NHA_dataset event group (event_group param) for grouping in GA4.
   * Params: event_group, dataset_name, dataset_id, dataset_label.
   */
  trackDatasetClick(datasetName: string, datasetId: string): void {
    if (typeof window.gtag !== 'function') {
      return;
    }
    const measurementId = environment.googleAnalyticsId;
    const safeName = (datasetName ?? '').trim() || 'Untitled';
    const safeId = (datasetId ?? '').trim() || '';
    const params: Record<string, string> = {
      event_group: MAHAAGX_DATASET_EVENT_GROUP,
      dataset_name: safeName,
      dataset_id: safeId,
      dataset_label: `${safeName}--${safeId}`,
    };
    if (measurementId) {
      params['send_to'] = measurementId;
    }
    window.gtag('event', MAHAAGX_GTAG_EVENTS.DATASET_CLICK, params);
  }

  /**
   * Send a request-access event to GA4 (event name: NHA_request_access).
   * Part of NHA_dataset event group (event_group param) for grouping in GA4.
   * Params: event_group, dataset_name, dataset_id, dataset_label.
   */
  trackRequestAccess(datasetName: string, datasetId: string): void {
    if (typeof window.gtag !== 'function') {
      return;
    }
    const measurementId = environment.googleAnalyticsId;
    const safeName = (datasetName ?? '').trim() || 'Untitled';
    const safeId = (datasetId ?? '').trim() || '';
    const params: Record<string, string> = {
      event_group: MAHAAGX_DATASET_EVENT_GROUP,
      dataset_name: safeName,
      dataset_id: safeId,
      dataset_label: `${safeName}--${safeId}`,
    };
    if (measurementId) {
      params['send_to'] = measurementId;
    }
    window.gtag('event', MAHAAGX_GTAG_EVENTS.REQUEST_ACCESS, params);
  }

  /** Build common params for NHA_dataset events. */
  private datasetEventParams(datasetName: string, datasetId: string): Record<string, string> {
    const safeName = (datasetName ?? '').trim() || 'Untitled';
    const safeId = (datasetId ?? '').trim() || '';
    const params: Record<string, string> = {
      event_group: MAHAAGX_DATASET_EVENT_GROUP,
      dataset_name: safeName,
      dataset_id: safeId,
      dataset_label: `${safeName}--${safeId}`,
    };
    const measurementId = environment.googleAnalyticsId;
    if (measurementId) {
      params['send_to'] = measurementId;
    }
    return params;
  }

  /** 1. Dataset download (main Download or Get Data CSV). Event: NHA_dataset_download. Count per day in GA4. */
  trackDatasetDownload(datasetName: string, datasetId: string): void {
    if (typeof window.gtag !== 'function') return;
    window.gtag('event', MAHAAGX_GTAG_EVENTS.DATASET_DOWNLOAD, this.datasetEventParams(datasetName, datasetId));
  }

  /** 2. Get Data button click. Event: NHA_dataset_get_data. */
  trackDatasetGetDataClick(datasetName: string, datasetId: string): void {
    if (typeof window.gtag !== 'function') return;
    window.gtag('event', MAHAAGX_GTAG_EVENTS.DATASET_GET_DATA, this.datasetEventParams(datasetName, datasetId));
  }

  /** 3. View File button click. Event: NHA_dataset_view_file. */
  trackDatasetViewFileClick(datasetName: string, datasetId: string): void {
    if (typeof window.gtag !== 'function') return;
    window.gtag('event', MAHAAGX_GTAG_EVENTS.DATASET_VIEW_FILE, this.datasetEventParams(datasetName, datasetId));
  }

  /** 4. Data descriptors button click. Event: NHA_dataset_data_descriptors. */
  trackDatasetDataDescriptorsClick(datasetName: string, datasetId: string): void {
    if (typeof window.gtag !== 'function') return;
    window.gtag('event', MAHAAGX_GTAG_EVENTS.DATASET_DATA_DESCRIPTORS, this.datasetEventParams(datasetName, datasetId));
  }

  /** 5. Sample data button click. Event: NHA_dataset_sample_data. */
  trackDatasetSampleDataClick(datasetName: string, datasetId: string): void {
    if (typeof window.gtag !== 'function') return;
    window.gtag('event', MAHAAGX_GTAG_EVENTS.DATASET_SAMPLE_DATA, this.datasetEventParams(datasetName, datasetId));
  }

  /** 6. View code button click. Event: NHA_dataset_view_code. */
  trackDatasetViewCodeClick(datasetName: string, datasetId: string): void {
    if (typeof window.gtag !== 'function') return;
    window.gtag('event', MAHAAGX_GTAG_EVENTS.DATASET_VIEW_CODE, this.datasetEventParams(datasetName, datasetId));
  }

  /** Catalogue: Model click. Event: NHA_model_click. */
  trackModelClick(modelName: string, modelId: string): void {
    if (typeof window.gtag !== 'function') return;
    const params = this.buildItemParams(modelName, modelId);
    window.gtag('event', MAHAAGX_GTAG_EVENTS.MODEL_CLICK, params);
  }

  /** Catalogue: Challenge click. Event: NHA_challenge_click. */
  trackChallengeClick(challengeName: string, challengeId: string): void {
    if (typeof window.gtag !== 'function') return;
    const params = this.buildItemParams(challengeName, challengeId);
    window.gtag('event', MAHAAGX_GTAG_EVENTS.CHALLENGE_CLICK, params);
  }

  /** Catalogue: Discussion created. Event: NHA_discussion_create. */
  trackDiscussionCreate(): void {
    if (typeof window.gtag !== 'function') return;
    const params: Record<string, string> = {};
    const measurementId = environment.googleAnalyticsId;
    if (measurementId) params['send_to'] = measurementId;
    window.gtag('event', MAHAAGX_GTAG_EVENTS.DISCUSSION_CREATE, params);
  }

  /** Consumer: My Projects page view. Event: NHA_my_project_view. */
  trackMyProjectView(): void {
    if (typeof window.gtag !== 'function') return;
    const params: Record<string, string> = {};
    const measurementId = environment.googleAnalyticsId;
    if (measurementId) params['send_to'] = measurementId;
    window.gtag('event', MAHAAGX_GTAG_EVENTS.MY_PROJECT_VIEW, params);
  }

  /** My Projects: notebook start. Event: NHA_my_project_notebook_start. */
  trackMyProjectNotebookStart(notebookName = ''): void {
    if (typeof window.gtag !== 'function') return;
    const params: Record<string, string> = {};
    if (notebookName) params['notebook_name'] = notebookName;
    const measurementId = environment.googleAnalyticsId;
    if (measurementId) params['send_to'] = measurementId;
    window.gtag('event', MAHAAGX_GTAG_EVENTS.MY_PROJECT_NOTEBOOK_START, params);
  }

  /** My Projects: notebook stop. Event: NHA_my_project_notebook_stop. */
  trackMyProjectNotebookStop(notebookName = ''): void {
    if (typeof window.gtag !== 'function') return;
    const params: Record<string, string> = {};
    if (notebookName) params['notebook_name'] = notebookName;
    const measurementId = environment.googleAnalyticsId;
    if (measurementId) params['send_to'] = measurementId;
    window.gtag('event', MAHAAGX_GTAG_EVENTS.MY_PROJECT_NOTEBOOK_STOP, params);
  }

  /** My Projects: notebook create. Event: NHA_my_project_notebook_create. */
  trackMyProjectNotebookCreate(notebookName = ''): void {
    if (typeof window.gtag !== 'function') return;
    const params: Record<string, string> = {};
    if (notebookName) params['notebook_name'] = notebookName;
    const measurementId = environment.googleAnalyticsId;
    if (measurementId) params['send_to'] = measurementId;
    window.gtag('event', MAHAAGX_GTAG_EVENTS.MY_PROJECT_NOTEBOOK_CREATE, params);
  }

  /** My Projects: notebook delete. Event: NHA_my_project_notebook_delete. */
  trackMyProjectNotebookDelete(notebookName = ''): void {
    if (typeof window.gtag !== 'function') return;
    const params: Record<string, string> = {};
    if (notebookName) params['notebook_name'] = notebookName;
    const measurementId = environment.googleAnalyticsId;
    if (measurementId) params['send_to'] = measurementId;
    window.gtag('event', MAHAAGX_GTAG_EVENTS.MY_PROJECT_NOTEBOOK_DELETE, params);
  }

  /** My Projects: request credits submitted. Event: NHA_my_project_request_credits. */
  trackMyProjectRequestCredits(): void {
    if (typeof window.gtag !== 'function') return;
    const params: Record<string, string> = {};
    const measurementId = environment.googleAnalyticsId;
    if (measurementId) params['send_to'] = measurementId;
    window.gtag('event', MAHAAGX_GTAG_EVENTS.MY_PROJECT_REQUEST_CREDITS, params);
  }

  /** Profile: Change password success. Event: NHA_profile_change_password. */
  trackProfileChangePassword(): void {
    if (typeof window.gtag !== 'function') return;
    const params: Record<string, string> = {};
    const measurementId = environment.googleAnalyticsId;
    if (measurementId) params['send_to'] = measurementId;
    window.gtag('event', MAHAAGX_GTAG_EVENTS.PROFILE_CHANGE_PASSWORD, params);
  }

  /** Profile: Get credentials (recredential) success. Event: NHA_profile_get_credentials. */
  trackProfileGetCredentials(): void {
    if (typeof window.gtag !== 'function') return;
    const params: Record<string, string> = {};
    const measurementId = environment.googleAnalyticsId;
    if (measurementId) params['send_to'] = measurementId;
    window.gtag('event', MAHAAGX_GTAG_EVENTS.PROFILE_GET_CREDENTIALS, params);
  }

  /** Profile: Delete account (user deleted account). Event: NHA_profile_delete_account. */
  trackProfileDeleteAccount(): void {
    if (typeof window.gtag !== 'function') return;
    const params: Record<string, string> = {};
    const measurementId = environment.googleAnalyticsId;
    if (measurementId) params['send_to'] = measurementId;
    window.gtag('event', MAHAAGX_GTAG_EVENTS.PROFILE_DELETE_ACCOUNT, params);
  }

  private buildItemParams(name: string, id: string): Record<string, string> {
    const safeName = (name ?? '').trim() || 'Untitled';
    const safeId = (id ?? '').trim() || '';
    const params: Record<string, string> = {
      item_name: safeName,
      item_id: safeId,
      item_label: `${safeName}--${safeId}`,
    };
    const measurementId = environment.googleAnalyticsId;
    if (measurementId) params['send_to'] = measurementId;
    return params;
  }
}
